import type { ChartRangePreset, ReadingsRange } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;
const PRESET_SPAN_MS: Record<Exclude<ChartRangePreset, 'custom'>, number> = {
  '24h': DAY_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
};

// Bucket `now` so the computed range (and thus the React Query key) stays stable
// across renders within the window, rather than churning every millisecond and
// triggering constant refetches. The window advances one bucket at a time.
const RANGE_BUCKET_MS = 60 * 1000;

export const RANGE_PRESET_OPTIONS: Array<{ value: ChartRangePreset; label: string }> = [
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: 'custom', label: 'Custom' },
];

export interface RangeInput {
  rangePreset: ChartRangePreset;
  fromTs?: string | null;
  toTs?: string | null;
}

// Resolve a card's preset (+ optional custom bounds) to an absolute range.
// A 'custom' preset without both bounds falls back to 7 days.
export function computeRange(card: RangeInput, nowMs: number): ReadingsRange {
  if (card.rangePreset === 'custom' && card.fromTs && card.toTs) {
    return { from: card.fromTs, to: card.toTs };
  }
  const span = PRESET_SPAN_MS[card.rangePreset === 'custom' ? '7d' : card.rangePreset];
  const bucket = Math.floor(nowMs / RANGE_BUCKET_MS) * RANGE_BUCKET_MS;
  // `to` is the END of the current bucket, not its start. Flooring both ends
  // kept the query key stable but left `to` up to a bucket in the past, so a
  // reading that had just arrived fell outside the range and the chart showed
  // "No readings in this range" until the next bucket. Extending to the bucket
  // end keeps the key just as stable (it still changes once per bucket) while
  // including readings recorded moments ago. The reads API places no upper
  // bound on `to`, so a `to` slightly in the future is fine.
  return {
    from: new Date(bucket - span).toISOString(),
    to: new Date(bucket + RANGE_BUCKET_MS).toISOString(),
  };
}
